import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  spaceContributionFormSchema,
  type SpaceContributionFormInput,
  type SpaceContributionFormValues,
} from './space-contribution-form-schema'
import type {
  SpaceContributionRow,
  ContributionSource,
  ContributionStatus,
} from '@/types/space-contributions'
import {
  CONTRIBUTION_SOURCE_LABELS,
  CONTRIBUTION_SOURCES,
  CONTRIBUTION_STATUS_LABELS,
  CONTRIBUTION_STATUSES,
} from '@/types/space-contributions'
import { useAllUnits } from '@/hooks/useHangarUnits'
import { useAircraftList } from '@/hooks/useAircraft'
import { useTenant } from '@/contexts'

interface SpaceContributionFormProps {
  spaceContribution?: SpaceContributionRow | null
  onSubmit: (data: SpaceContributionFormValues) => void
  onCancel: () => void
  isLoading?: boolean
}

export function SpaceContributionForm({
  spaceContribution,
  onSubmit,
  onCancel,
  isLoading,
}: SpaceContributionFormProps) {
  useTenant() // Ensure tenant context is available
  const { data: hangarUnits = [] } = useAllUnits()
  const { data: aircraft = [] } = useAircraftList()
  const form = useForm<SpaceContributionFormInput, unknown, SpaceContributionFormValues>({
    resolver: zodResolver(spaceContributionFormSchema),
    defaultValues: {
      unit_id: spaceContribution?.unit_id || '',
      source_type: spaceContribution?.source_type || ('aircraft_away' as ContributionSource),
      available_from:
        spaceContribution?.available_from?.split('T')[0] ||
        new Date().toISOString().split('T')[0],
      available_until:
        spaceContribution?.available_until?.split('T')[0] ||
        new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      sqft: spaceContribution?.sqft || 0,
      wingspan: spaceContribution?.wingspan ?? undefined,
      length: spaceContribution?.length ?? undefined,
      position_x: spaceContribution?.position_x ?? undefined,
      position_y: spaceContribution?.position_y ?? undefined,
      aircraft_id: spaceContribution?.aircraft_id || '',
      flight_schedule_id: spaceContribution?.flight_schedule_id || '',
      lease_contract_id: spaceContribution?.lease_contract_id || '',
      status: spaceContribution?.status || ('projected' as ContributionStatus),
    },
  })

  const isEdit = !!spaceContribution
  const sourceType = form.watch('source_type')

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          {/* Basic Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            {/* Unit Selection */}
            <FormField
              control={form.control}
              name="unit_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a unit" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {hangarUnits.map((unit) => (
                        <SelectItem key={unit.id} value={unit.id}>
                          {unit.unit_code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Source Type */}
            <FormField
              control={form.control}
              name="source_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTRIBUTION_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>
                          {CONTRIBUTION_SOURCE_LABELS[source]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Reason for space availability
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Aircraft (conditionally shown) */}
            {sourceType === 'aircraft_away' && (
              <FormField
                control={form.control}
                name="aircraft_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Aircraft {sourceType === 'aircraft_away' ? '*' : ''}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select aircraft" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {aircraft.map((ac) => (
                          <SelectItem key={ac.id} value={ac.id}>
                            {ac.tail_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Aircraft that will be away
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Status */}
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTRIBUTION_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {CONTRIBUTION_STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4 mt-4">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="available_from"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Available From *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="available_until"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Available Until *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Space Dimensions */}
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="sqft"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Square Feet *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="1500"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="wingspan"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Wingspan (ft)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="0"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="length"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Length (ft)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="0"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Position (optional) */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="position_x"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Position X</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="0"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>Map coordinate</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="position_y"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Position Y</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="0"
                        value={field.value != null ? String(field.value) : ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        name={field.name}
                      />
                    </FormControl>
                    <FormDescription>Map coordinate</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Optional relationships */}
            <FormField
              control={form.control}
              name="lease_contract_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lease Contract</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Contract ID (optional)"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormDescription>
                    Link to lease contract if applicable
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>
        </Tabs>

        {/* Form actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? 'Saving...'
              : isEdit
              ? 'Update Space Contribution'
              : 'Create Space Contribution'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
